import { useState } from 'react';
import { Button, Grid, ScreenReadersOnly, Stack } from '@customink/pigment-react';
import { usePage } from '~/adapters/browser/usePage';
import { usePostalAddresses } from '~/adapters/services/accounts/postalAddresses/hooks';
import { PostalAddress } from '~/adapters/services/accounts/postalAddresses/types';
import { Loader } from '~/components/Loader/loader';
import { CushionedBox } from '~/components/Layout/CushionedBox';
import { EmptyFunction } from '~/adapters/typescript/emptyFunction';
import { Title } from '../../title';
import { PostalAddressCard } from './PostalAddressCard';
import { NewAddressDialog } from './AddressDialog/NewAddressDialog';

type EmptyAddressCardListProps = { onOpen: EmptyFunction };
const EmptyAddressCardList = ({ onOpen }: EmptyAddressCardListProps) => (
    <CushionedBox>
        <p>
            Simply fill in the details and save your addresses here. This will make it easy for you to use saved
            addresses in the future.
        </p>
        <Button onClick={onOpen} variant="primary">
            Add a New Address
        </Button>
    </CushionedBox>
);

type AddressCardListProps = EmptyAddressCardListProps & { addresses: PostalAddress[] };
const AddressCardList = ({ addresses, onOpen }: AddressCardListProps) => (
    <>
        <Stack direction="row" justifyContent="right" mb={3}>
            <Button onClick={onOpen} variant="secondary">
                Add a New Address
            </Button>
        </Stack>
        <Grid container spacing={2}>
            {addresses.map((address) => (
                <Grid item sm={12} md={6} xl={4} sx={{ display: 'grid' }} key={address.id}>
                    <PostalAddressCard address={address} />
                </Grid>
            ))}
        </Grid>
    </>
);

export function PostalAddressesTab() {
    usePage({ title: Title, reportPath: '/accounts/addresses' });
    const [dialogOpen, setDialogOpen] = useState(false);
    const onOpen = () => setDialogOpen(true);
    const onClose = () => setDialogOpen(false);
    const { data: addresses, isLoading } = usePostalAddresses();

    if (isLoading || !addresses) {
        return <Loader title="Loading..." devLabel="Postal addresses API" />;
    }

    return (
        <>
            <ScreenReadersOnly>
                <h2>Postal Addresses</h2>
            </ScreenReadersOnly>

            {addresses.length ? (
                <AddressCardList addresses={addresses} onOpen={onOpen} />
            ) : (
                <EmptyAddressCardList onOpen={onOpen} />
            )}
            {/* we want to unmount the React Component when closed, so that the form state resets when it's reopened */}
            {dialogOpen && <NewAddressDialog open={dialogOpen} onClose={onClose} />}
        </>
    );
}
